import { clamp, damp, dampAngle, type Vec2 } from '@jianghu/shared';
import * as THREE from 'three';

/**
 * Isometric-style follow camera: fixed pitch, rotatable yaw (Q/E or right-drag), wheel zoom,
 * critically-damped follow and trauma-based screen shake.
 */
export class CameraController {
  readonly camera: THREE.PerspectiveCamera;
  yaw = Math.PI / 4;
  private targetYaw = Math.PI / 4;
  pitch = 0.93;
  distance = 21;
  private targetDistance = 21;
  private readonly focus = new THREE.Vector3();
  private initialized = false;
  private trauma = 0;
  private t = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(38, aspect, 0.5, 900);
  }

  setDistance(d: number): void {
    this.targetDistance = clamp(d, 9, 36);
  }

  zoom(deltaY: number): void {
    this.targetDistance = clamp(this.targetDistance * (1 + deltaY * 0.0012), 9, 36);
  }

  rotate(radians: number): void {
    this.targetYaw += radians;
  }

  shake(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  snapTo(x: number, y: number, z: number): void {
    this.focus.set(x, y, z);
    this.initialized = true;
  }

  get zoomDistance(): number {
    return this.targetDistance;
  }

  update(dt: number, fx: number, fy: number, fz: number): void {
    this.t += dt;
    if (!this.initialized) this.snapTo(fx, fy, fz);
    this.focus.x = damp(this.focus.x, fx, 10, dt);
    this.focus.y = damp(this.focus.y, fy, 10, dt);
    this.focus.z = damp(this.focus.z, fz, 10, dt);
    this.yaw = dampAngle(this.yaw, this.targetYaw, 10, dt);
    this.distance = damp(this.distance, this.targetDistance, 8, dt);
    // Slightly flatter angle when zoomed in, steeper when zoomed out.
    const pitch = this.pitch + (this.distance - 21) * 0.008;
    const horiz = Math.cos(pitch) * this.distance;
    const cx = this.focus.x + Math.sin(this.yaw) * horiz;
    const cz = this.focus.z + Math.cos(this.yaw) * horiz;
    const cy = this.focus.y + Math.sin(pitch) * this.distance;
    let sx = 0;
    let sy = 0;
    if (this.trauma > 0) {
      const s = this.trauma * this.trauma * 0.35;
      sx = (Math.sin(this.t * 61) + Math.sin(this.t * 37)) * 0.5 * s;
      sy = (Math.sin(this.t * 53 + 1) + Math.sin(this.t * 29)) * 0.5 * s;
      this.trauma = Math.max(0, this.trauma - dt * 1.8);
    }
    this.camera.position.set(cx + sx, cy + sy, cz);
    this.camera.lookAt(this.focus.x + sx * 0.5, this.focus.y + 1.0 + sy * 0.5, this.focus.z);
  }

  /** Ground-plane unit vectors for camera-relative movement. */
  forward(): Vec2 {
    return { x: -Math.sin(this.yaw), z: -Math.cos(this.yaw) };
  }

  right(): Vec2 {
    return { x: Math.cos(this.yaw), z: -Math.sin(this.yaw) };
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
